import axios from 'axios';

// Crear instancia de axios
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000, // Aumentado a 30 segundos para redes lentas
});

// Rutas donde se envía comando/notificación y queremos auditoría con usuario logueado (opcional)
const rutasConAuditoriaUsuario = [
  (url) => url.includes('/comando/'),
  (url) => url.includes('/comando-todos'),
  (url) => url.includes('/enviar-notificacion'),
  (url) => /\/dispositivos\/\d+\/desaprovisionar/.test(url),
];

// Interceptor para agregar tokens a las peticiones
api.interceptors.request.use(
  (config) => {
    // Rutas que requieren Bearer token de API (dispositivos, comandos, sync, comando, códigos desbloqueo)
    const apiProtectedRoutes = ['/dispositivos', '/comandos', '/dispositivo/sync', '/comando/', '/generar-codigos-desbloqueo/', '/obtener-codigo-desbloqueo/'];
    const isApiProtectedRoute = apiProtectedRoutes.some(route => config.url.includes(route));
    
    if (isApiProtectedRoute) {
      // Agregar Bearer token de API para rutas protegidas
      const apiToken = process.env.REACT_APP_BEARER_TOKEN_APIS_FRONT;
      if (apiToken) {
        config.headers.Authorization = `Bearer ${apiToken}`;
      }
      // Si hay usuario logueado, añadir datos para auditoría (opcional). Si no se envían, el backend registra "API/Sistema"
      if (rutasConAuditoriaUsuario.some(fn => fn(config.url)) && (config.method === 'post' || config.method === 'put')) {
        try {
          const usuarioStr = localStorage.getItem('usuario');
          if (usuarioStr) {
            const usuario = JSON.parse(usuarioStr);
            const data = config.data != null && typeof config.data === 'object' ? { ...config.data } : {};
            if (usuario.id != null) data.auditoria_usuario_id = usuario.id;
            if (usuario.nombre_usuario) data.auditoria_nombre_usuario = usuario.nombre_usuario;
            config.data = data;
          }
        } catch (_) {}
      }
    } else {
      // Para otras rutas (auth, usuarios, códigos), usar JWT de usuario
      const userToken = localStorage.getItem('token');
      if (userToken) {
        config.headers.Authorization = `Bearer ${userToken}`;
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Login fallido (credenciales inválidas / usuario inactivo): no recargar la página
      const esLogin = url.includes('/auth/login');
      // Rutas de API con Bearer propio: no forzar redirect a /login
      const isApiProtectedRoute = ['/dispositivos', '/comandos', '/dispositivo/sync'].some(route =>
        url.includes(route)
      );

      if (!esLogin && !isApiProtectedRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('usuario');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Servicios de autenticación
export const authService = {
  login: (credenciales) => api.post('/auth/login', credenciales),
  verificar: () => api.get('/auth/verificar-token'),
  cambiarContrasena: (datos) => api.put('/auth/cambiar-contrasena', datos),
};

// Servicios de dispositivos
export const dispositivosService = {
  obtenerTodos: (params) => api.get('/dispositivos', { params }),
  obtenerPorId: (id) => api.get(`/dispositivos/${id}`),
  crear: (datos) => api.post('/dispositivos', datos),
  actualizar: (id, datos) => api.put(`/dispositivos/${id}`, datos),
  eliminar: (id) => api.delete(`/dispositivos/${id}`),
  heartbeat: (id, datos) => api.post(`/dispositivos/${id}/heartbeat`, datos),
  estadisticas: () => api.get('/dispositivos/estadisticas/globales', { timeout: 60000 }), // 60 segundos para esta petición específica
  // Endpoints individuales para diagnóstico
  estadisticasTotal: () => api.get('/dispositivos/estadisticas/total', { timeout: 60000 }),
  estadisticasConectados: () => api.get('/dispositivos/estadisticas/conectados', { timeout: 60000 }),
  estadisticasActivos: () => api.get('/dispositivos/estadisticas/activos', { timeout: 60000 }),
  estadisticasComandosPendientes: () => api.get('/dispositivos/estadisticas/comandos-pendientes', { timeout: 60000 }),
  estadisticasBloqueados: () => api.get('/dispositivos/estadisticas/bloqueados', { timeout: 60000 }),
  desaprovisionar: (id) => api.post(`/dispositivos/${id}/desaprovisionar`),
  obtenerCodigoDesbloqueo: (deviceCode) => api.post(`/obtener-codigo-desbloqueo/${deviceCode}`),
  generarCodigosDesbloqueo: (deviceCode) => api.post(`/generar-codigos-desbloqueo/${deviceCode}`),
};

// Servicios de comandos
export const comandosService = {
  obtenerTodos: (params) => api.get('/comandos', { params }),
  obtenerPorId: (id) => api.get(`/comandos/${id}`),
  crear: (datos) => api.post('/comandos', datos),
  crearMultiple: (datos) => api.post('/comandos/multiple', datos),
  actualizar: (id, datos) => api.put(`/comandos/${id}`, datos),
  cancelar: (id) => api.delete(`/comandos/${id}`),
  obtenerPendientes: (dispositivoId) => api.get(`/comandos/dispositivo/${dispositivoId}/pendientes`),
  obtenerTipos: () => api.get('/comandos/tipos/disponibles'),
};

// Servicios de usuarios
export const usuariosService = {
  obtenerTodos: (params) => api.get('/usuarios', { params }),
  obtenerPorId: (id) => api.get(`/usuarios/${id}`),
  crear: (datos) => api.post('/usuarios', datos),
  actualizar: (id, datos) => api.put(`/usuarios/${id}`, datos),
  cambiarContrasena: (id, datos) => api.put(`/usuarios/${id}/cambiar-contrasena`, datos),
  eliminar: (id) => api.delete(`/usuarios/${id}`),
  estadisticas: () => api.get('/usuarios/estadisticas/globales'),
};

// Servicios de códigos (licencias)
export const codigosService = {
  obtenerTodos: (params) => api.get('/codigos', { params }),
  obtenerPorId: (id) => api.get(`/codigos/${id}`),
  crear: (datos) => api.post('/codigos', datos),
  verificar: (codigo) => api.post('/codigos/verificar', { codigo }),
  marcarUtilizado: (id) => api.put(`/codigos/${id}/utilizar`),
  marcarNoUtilizado: (id) => api.put(`/codigos/${id}/liberar`),
  eliminar: (id) => api.delete(`/codigos/${id}`),
  estadisticas: () => api.get('/codigos/estadisticas/totales'),
  generar: (cantidad) => api.post('/codigos/generar', { cantidad }),
  exportar: (formato) => api.get('/codigos/exportar', { params: { formato } }),
};



// Servicio de auditoría (solo disponible para usuarios autorizados)
export const auditoriaService = {
  listar: (params) => api.get('/auditoria', { params }),
};

// Servicio de salud
export const saludService = {
  verificar: () => api.get('/salud'),
};

export { api }; 